import { GeneralCatalogItem } from 'data-layer-cr/dist/models';
import { MAX_SHAREHOLDERS } from 'src/constants/shareholders';
import { findNameByCode } from 'src/functions/mapCatatalogToDisplay';
import EmissionDataFields, {
  Shareholder,
  makeEmptyEmissionDataFields,
} from 'src/models/helperTypes/EmissionDataFields';
import FinalBeneficiary from 'src/models/helperTypes/FinalBeneficiary';
import FormShape from '../FormShape';

export function parseFinalBeneficiaryData(data: FormShape): FinalBeneficiary {
  return {
    firstName: data.beneficiaryFirstName,
    lastName: data.beneficiaryLastName,
    idType: data.beneficiaryIdType,
    id: data.beneficiaryIdNumber,
    sharePercentage: data.beneficiarySharePercentage,
    birthdate: data.beneficiaryBirthdate,
    nationalityId: data.beneficiaryBirthPlaceId,
    nationality: data.beneficiaryBirthPlace,
    birthPlaceId: data.beneficiaryBirthPlaceId,
    birthPlace: data.beneficiaryBirthPlace,
    occupation: data.beneficiaryOccupation,
    occupationName: data.beneficiaryOccupationName,
    economicActivity: data.economicActivity,
    economicActivityName: data.economicActivityName,
    companyRole: data.beneficiaryCompanyRole,
    phone1: data.beneficiaryPhone1,
    phone2: data.beneficiaryPhone2,
    addressDetail: data.beneficiaryAddressDetail,
    countryId: data.beneficiaryCountryId,
    country: data.beneficiaryCountry,
    stateId: data.beneficiaryStateId,
    state: data.beneficiaryState,
    cityId: data.beneficiaryCityId,
    city: data.beneficiaryCity,
    townshipId: data.beneficiaryTownshipId,
    township: data.beneficiaryTownship,
  };
}

export function parseEmissionData(
  data: FormShape,
  countries: GeneralCatalogItem[],
  civilStatuses: GeneralCatalogItem[],
): EmissionDataFields {
  const helper = new Array(data.totalShareHolder).fill(0);
  const shareholders: Shareholder[] = [];
  helper.forEach((_, index) => {
    const nationalityCode = data[
      `share${index}nationality` as keyof FormShape
    ] as string;
    const civilStatusCode = data[
      `share${index}civilStatus` as keyof FormShape
    ] as string;

    const shareholder: Shareholder = {
      firstName: data[`share${index}firstName` as keyof FormShape] as string,
      lastName: data[`share${index}lastName` as keyof FormShape] as string,
      gender: data[`share${index}gender` as keyof FormShape] as string,
      birthday: data[`share${index}birthday` as keyof FormShape] as string,
      nationality: nationalityCode,
      nationalityName: findNameByCode(countries, nationalityCode),
      civilStatus: civilStatusCode,
      civilStatusName: findNameByCode(civilStatuses, civilStatusCode),
      idType: data[`share${index}idType` as keyof FormShape] as string,
      id: data[`share${index}id` as keyof FormShape] as string,
      participationRate: data[
        `share${index}participationRate` as keyof FormShape
      ] as string,
    };
    shareholders.push(shareholder);
  });

  return {
    ...makeEmptyEmissionDataFields(),
    idType: data.idType,
    email: data.email,
    phoneNumber: data.phoneNumber,
    idNumber: data.idNumber,
    names: data.names,
    lastNames: data.lastNames,
    birthday: data.birthday,
    birthPlace: data.birthPlace,
    birthPlaceName: data.birthPlaceName,
    gender: data.gender,
    legalEntityName: data.legalEntityName,
    rucNumber: data.rucNumber,
    legalEntityCountry: data.legalEntityCountry,
    legalEntityOccupation: data.legalEntityOccupation,
    originType: data.originType,
    salary: data.salary,
    typeOfMoney: data.typeOfMoney,
    economicActivity: data.economicActivity,
    economicActivityName: data.economicActivityName,
    originCountry: data.originCountry,
    creditorType: data.creditorType,
    currentBank: data.currentBank,
    creditAmount: data.creditAmount,
    pep: data.pep,
    bloodRelatedToPep: data.bloodRelatedToPep,
    cicac: data.cicac,
    pepLawArticle: data.pepLawArticle,
    enableInheritor: data.enableInheritor,
    effectiveDate: data.effectiveDate,
    assignDateSignature: data.assignDateSignature,

    socialReason: data.socialReason,
    fixNumber: data.fixNumber,
    notificationPreference: data.notificationPreference,
    shareholders,

    // Stock Exchange data
    hasShareholders: data.hasShareholders,
    isInStockExchange: data.isInStockExchange,
    stockExchangeName: data.stockExchangeName,
    stockExchangeCountry: data.stockExchangeCountry,
    stockExchangeCountryName: data.stockExchangeCountryName,
    companyType: data.companyType,

    addressCountry: data.addressCountry,
    addressCountryName: data.addressCountryName,
    addressState: data.addressState,
    addressStateName: data.addressStateName,
    addressCity: data.addressCity,
    addressCityName: data.addressCityName,
    addressTownship: data.addressTownship,
    addressTownshipName: data.addressTownshipName,
    addressDetail: data.addressDetail,
  };
}

type InitialFormData = {
  dataFields: EmissionDataFields;
  beneficiary: FinalBeneficiary;
  personExists: boolean;
};

export function parseInitialFormData({
  dataFields,
  beneficiary,
  personExists,
}: InitialFormData): FormShape {
  const {
    firstName,
    lastName,
    idType,
    id,
    sharePercentage,
    birthdate,
    birthPlaceId,
    birthPlace,
    occupation,
    occupationName,
    companyRole,
    phone1,
    phone2,
    addressDetail,
    countryId,
    country,
    stateId,
    state,
    cityId,
    city,
    townshipId,
    township,
  } = beneficiary;

  const shareHolderData: { [x: string]: string } = {};
  const helper = new Array(MAX_SHAREHOLDERS).fill(0);
  helper.forEach((_, index) => {
    shareHolderData[`share${index}firstName`] = '';
    shareHolderData[`share${index}lastName`] = '';
    shareHolderData[`share${index}gender`] = '';
    shareHolderData[`share${index}birthday`] = '';
    shareHolderData[`share${index}nationality`] = '';
    shareHolderData[`share${index}nationalityName`] = '';
    shareHolderData[`share${index}civilStatus`] = '';
    shareHolderData[`share${index}civilStatusName`] = '';
    shareHolderData[`share${index}idType`] = 'N';
    shareHolderData[`share${index}id`] = '';
    shareHolderData[`share${index}participationRate`] = '';
  });

  dataFields.shareholders.forEach((shareHolder, index) => {
    shareHolderData[`share${index}firstName`] = shareHolder.firstName;
    shareHolderData[`share${index}lastName`] = shareHolder.lastName;
    shareHolderData[`share${index}gender`] = shareHolder.gender;
    shareHolderData[`share${index}birthday`] = shareHolder.birthday;
    shareHolderData[`share${index}nationality`] = shareHolder.nationality;
    shareHolderData[`share${index}nationalityName`] =
      shareHolder.nationalityName;
    shareHolderData[`share${index}civilStatus`] = shareHolder.civilStatus;
    shareHolderData[`share${index}civilStatusName`] =
      shareHolder.civilStatusName;
    shareHolderData[`share${index}idType`] = shareHolder.idType;
    shareHolderData[`share${index}id`] = shareHolder.id;
    shareHolderData[`share${index}participationRate`] =
      shareHolder.participationRate;
  });

  const pepInitialValue =
    dataFields.idNumber === '' ? undefined : dataFields.pep;
  const bloodRelatedToPepInitialValue =
    dataFields.idNumber === '' ? undefined : dataFields.bloodRelatedToPep;
  const pepLawArticleInitialValue =
    dataFields.idNumber === '' ? undefined : dataFields.pepLawArticle;

  const formFields = {
    ...dataFields,

    pep: pepInitialValue,
    bloodRelatedToPep: bloodRelatedToPepInitialValue,
    pepLawArticle: pepLawArticleInitialValue,

    personExists,
    /**
     * some of the name properties in dataFields overlaps with beneficiary, remap
     * note that the name format follows <prefixKey><inputName> used in the
     * subcomponent FinalBeneficiaryCard
     */
    beneficiaryFirstName: firstName,
    beneficiaryLastName: lastName,
    beneficiaryIdType: idType,
    beneficiaryIdNumber: id,
    beneficiarySharePercentage: sharePercentage,
    beneficiaryBirthdate: birthdate,
    beneficiaryNationalityId: birthPlaceId,
    beneficiaryNationality: birthPlace,
    beneficiaryBirthPlaceId: birthPlaceId,
    beneficiaryBirthPlace: birthPlace,
    beneficiaryOccupation: occupation,
    beneficiaryOccupationName: occupationName,
    beneficiaryCompanyRole: companyRole,
    beneficiaryPhone1: phone1,
    beneficiaryPhone2: phone2,
    beneficiaryAddressDetail: addressDetail,
    beneficiaryCountryId: countryId,
    beneficiaryCountry: country,
    beneficiaryStateId: stateId,
    beneficiaryState: state,
    beneficiaryCityId: cityId,
    beneficiaryCity: city,
    beneficiaryTownshipId: townshipId,
    beneficiaryTownship: township,

    totalShareHolder: Math.max(dataFields.shareholders.length, 1),
    ...shareHolderData,
  };

  return formFields as unknown as FormShape;
}
